import React, { useMemo, useState } from 'react';
import { serializeEntityValue } from '../utils/entityValue';
import './EntityValueCell.css';

export interface EntityValueCellProps {
  /** String, number, object, or array — objects are JSON-serialized for display/expand. */
  value: unknown;
  emptyLabel?: string;
  /** CSS class for the truncated inline value (preserves existing card styles). */
  valueClassName?: string;
}

function tryParseJson(raw: string): unknown | null {
  const t = raw.trim();
  if (!t || (t[0] !== '{' && t[0] !== '[')) return null;
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

/** Non-empty objects/arrays can expand; empty [] / {} stay compact. */
function isExpandableJson(parsed: unknown): boolean {
  if (parsed == null) return false;
  if (Array.isArray(parsed)) return parsed.length > 0;
  if (typeof parsed === 'object') return Object.keys(parsed as object).length > 0;
  return false;
}

/**
 * Compact entity value: single-line ellipsis by default (same visual as before).
 * - Hover: full value via native title (copyable text for plain values).
 * - Click: expand only for JSON object/array or long plain strings.
 */
const EntityValueCell: React.FC<EntityValueCellProps> = ({
  value,
  emptyLabel = '· · ·',
  valueClassName = '',
}) => {
  const [expanded, setExpanded] = useState(false);

  const raw = serializeEntityValue(value) ?? '';
  const isEmpty = !raw;
  const parsed = useMemo(() => (raw ? tryParseJson(raw) : null), [raw]);
  const jsonExpandable = isExpandableJson(parsed);
  const longPlain = !jsonExpandable && !isEmpty && raw.length > 40;
  const canExpand = jsonExpandable || longPlain;
  const showHoverTip = !isEmpty && (canExpand || raw.length > 20);

  const pretty = useMemo(() => {
    if (!jsonExpandable || parsed == null) return raw;
    try {
      return JSON.stringify(parsed, null, 2);
    } catch {
      return raw;
    }
  }, [jsonExpandable, parsed, raw]);

  if (isEmpty) {
    return (
      <span className={`entity-value-cell entity-value-cell--plain ${valueClassName}`.trim()}>
        {emptyLabel}
      </span>
    );
  }

  // Plain short values: normal selectable text (no button, so copy works).
  if (!canExpand) {
    return (
      <span
        className={`entity-value-cell entity-value-cell--plain ${valueClassName}`.trim()}
        title={showHoverTip ? raw : undefined}
      >
        {raw}
      </span>
    );
  }

  return (
    <div className={`entity-value-cell entity-value-cell--expandable${expanded ? ' is-expanded' : ''}`}>
      <div className="entity-value-cell-main">
        <span
          className={`entity-value-cell-text ${valueClassName}`.trim()}
          title={showHoverTip ? raw : undefined}
        >
          {raw}
        </span>
        <button
          type="button"
          className="entity-value-cell-toggle"
          aria-label={expanded ? 'Collapse value' : 'Expand value'}
          aria-expanded={expanded}
          title={expanded ? 'Collapse' : 'Expand'}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? '▾' : '▸'}
        </button>
      </div>
      {expanded && (
        <div className="entity-value-cell-expanded" tabIndex={0}>
          {pretty}
        </div>
      )}
    </div>
  );
};

export default EntityValueCell;
